import * as React from 'react'
import { StyleSheet, Animated, Dimensions, Easing, Platform } from 'react-native'

// Helpers
const randomValue = (min, max) => Math.random() * (max - min) + min
const randomColor = (colors) => colors[Math.round(randomValue(0, colors.length - 1))]

// Defaults
const TOP_MIN = 0.7
const DEFAULT_COLORS = [
  '#e67e22',
  '#2ecc71',
  '#3498db',
  '#84AAC2',
  '#E6D68D',
  '#F67933',
  '#42A858',
  '#4F50A2',
  '#A86BB7',
  '#e74c3c',
  '#1abc9c',
]
const DEFAULT_EXPLOSION_SPEED = 350
const DEFAULT_FALL_SPEED = 3000

const Paper = (props: any) => {
  const [isRounded] = React.useState<boolean>(Math.round(randomValue(0, 1)) === 1)
  const { containerTransform, transform, opacity, color } = props
  const containerStyle = { transform: containerTransform }
  const style = {
    width: randomValue(8, 16),
    height: randomValue(8, 16),
    backgroundColor: color,
    transform,
    opacity,
  }

  return (
    <Animated.View
      pointerEvents="none"
      renderToHardwareTextureAndroid={true}
      style={[styles.confetti, containerStyle]}
    >
      <Animated.View style={[isRounded && styles.rounded, style]} />
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  confetti: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    zIndex: 999,
  },
  rounded: {
    borderRadius: 100,
  },
})

export const Confetti = (props: any) => {
  const {
    explosionSpeed = DEFAULT_EXPLOSION_SPEED,
    fallSpeed = DEFAULT_FALL_SPEED,
    onAnimationStart,
    onAnimationEnd,
    colors = DEFAULT_COLORS,
    count = 200,
    origin = { x: 245, y: 0 },
    fadeOut = true,
    height = Dimensions.get('window').height,
    width = Dimensions.get('window').width,
    delay = 0,
  } = props

  const [items, setItems] = React.useState<any>([])

  const animation = React.useRef(new Animated.Value(0)).current
  const sequence = React.useRef(
    Animated.sequence([
      Animated.timing(animation, { toValue: 0, duration: 0, useNativeDriver: true }),
      Animated.timing(animation, {
        toValue: 1,
        duration: explosionSpeed,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }),
      Animated.timing(animation, {
        toValue: 2,
        duration: fallSpeed,
        easing: Easing.quad,
        useNativeDriver: true,
      }),
    ]),
  ).current

  const start = () => {
    onAnimationStart && onAnimationStart()
    sequence.start(({ finished }) => {
      if (finished) {
        onAnimationEnd && onAnimationEnd()
      }
    })
  }

  React.useEffect(() => {
    const newItems = Array(count)
      .fill(null)
      .map(() => ({
        leftDelta: randomValue(0, 1),
        topDelta: randomValue(TOP_MIN, 1),
        swingDelta: randomValue(0.2, 1),
        speedDelta: {
          rotateX: randomValue(0.3, 1),
          rotateY: randomValue(0.3, 1),
          rotateZ: randomValue(0.3, 1),
        },
        color: randomColor(colors),
      }))

    setItems(
      newItems.map((item) => ({
        ...item,
        color: randomColor(colors),
      })),
    )
    setTimeout(start, delay)
  }, [])

  const getTransform = (item) => {
    const left = animation.interpolate({
      inputRange: [0, 1, 2],
      outputRange: [origin.x, item.leftDelta * width, item.leftDelta * width],
    })
    const top = animation.interpolate({
      inputRange: [0, 1, 1 + item.topDelta, 2],
      outputRange: [-origin.y, -item.topDelta * height, 0, 0],
    })
    const rotateX = animation.interpolate({
      inputRange: [0, 2],
      outputRange: ['0deg', `${item.speedDelta.rotateX * 360 * 10}deg`],
    })
    const rotateY = animation.interpolate({
      inputRange: [0, 2],
      outputRange: ['0deg', `${item.speedDelta.rotateY * 360 * 5}deg`],
    })
    const rotateZ = animation.interpolate({
      inputRange: [0, 2],
      outputRange: ['0deg', `${item.speedDelta.rotateZ * 360 * 2}deg`],
    })
    const translateX = animation.interpolate({
      inputRange: [0, 0.4, 1.2, 2],
      outputRange: [0, -(item.swingDelta * 30), item.swingDelta * 30, 0],
    })
    return {
      transform: [{ rotateX }, { rotateY }, { rotate: rotateZ }, { translateX }],
      containerTransform: [{ translateX: left }, { translateY: top }],
    }
  }

  return items?.length === 0 ? null : (
    <>
      {items.map((item, index) => {
        const opacity = animation.interpolate({
          inputRange: [0, 1, 1.8, 2],
          outputRange: [1, 1, 1, fadeOut ? 0 : 1],
        })
        const { transform, containerTransform } = getTransform(item) as any
        if (Platform.OS === 'android') {
          transform.push({ perspective: 100 })
        }
        return (
          <Paper
            color={item.color}
            containerTransform={containerTransform}
            transform={transform}
            opacity={opacity}
            key={index}
            testID={`confetti-${index + 1}`}
          />
        )
      })}
    </>
  )
}
